import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, notification } from 'antd';
import { useQueryClient } from '@tanstack/react-query';

import CreateUpdateProductForm from '../../components/CreateUpdateProductForm';

import { useFetch } from '../../hooks';
import { useAddons } from '../../hooks/useMenuQuery';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getMenuId } from '../../utils/helpers';
import axios from 'axios';
import store from 'store';
import { baseURL } from '../../utils/helpers';

const authToken = store.get('authenticationToken');

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productId = id;
  const { setHeaderComponent } = useContext(LayoutContext);
  const { doFetch: updateProductFetch } = useFetch();
  const { doFetch: uploadProductImageFetch } = useFetch();
  const { doFetch: fetchProductDetails } = useFetch();
  const { doFetch: fetchCategories } = useFetch();
  const { doFetch: fetchSubcategories } = useFetch();
  const { doFetch: fetchBrands } = useFetch();
  const { doFetch: fetchLanguages } = useFetch();

  // Data states
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  const [subcategoryList, setSubcategoryList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search states
  const [searchCategories, setSearchCategories] = useState([]);
  const [searchSubcategories, setSearchSubcategories] = useState([]);
  const [searchBrands, setSearchBrands] = useState([]);
  const [isSearchingCategories, setIsSearchingCategories] = useState(false);
  const [isSearchingSubcategories, setIsSearchingSubcategories] = useState(false);
  const [isSearchingBrands, setIsSearchingBrands] = useState(false);

  const menuId = getMenuId();
  const { data: addonsData } = useAddons(menuId, 1, 500);

  // Load all required data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        if (!menuId) {
          console.error('Menu ID not found');
          setIsLoading(false);
          return;
        }

        // Helper function to properly extract data from different response formats
        const extractResponseData = (response, expectedKeys = [], isArrayResponse = false) => {
          if (!response) return response;
          
          // If this is expected to be an array response
          if (isArrayResponse) {
            // If response is already an array, return it
            if (Array.isArray(response)) return response;
            // If response.data is an array, return it
            if (Array.isArray(response.data)) return response.data;
            // Otherwise return as-is
            return response;
          }
          
          // For object responses
          // If response has a 'data' property and it contains our expected keys, return it
          if (response.data && typeof response.data === 'object' && !Array.isArray(response.data) && expectedKeys.some(key => key in response.data)) {
            return response.data;
          }
          
          // If response directly contains our expected keys, return it as-is
          if (typeof response === 'object' && !Array.isArray(response) && expectedKeys.some(key => key in response)) {
            return response;
          }
          
          // Fall back to response.data if it exists, otherwise return response
          return response.data || response;
        };

        // Load all data in parallel
        const promises = [
          // Languages
          new Promise((resolve, reject) => {
            fetchLanguages({
              url: URLS.languages,
              onSuccess: (data) => {
                try {
                  const extracted = extractResponseData(data, ['id', 'name'], true); // true = isArrayResponse
                  resolve(Array.isArray(extracted) ? extracted : (Array.isArray(data) ? data : [extracted]));
                } catch (e) {
                  reject(e);
                }
              },
              onError: reject,
            });
          }),

          // Categories
          new Promise((resolve, reject) => {
            fetchCategories({
              url: URLS.getAllCategories(menuId),
              onSuccess: (data) => {
                try {
                  const extracted = extractResponseData(data, ['categories']);
                  resolve(extracted);
                } catch (e) {
                  reject(e);
                }
              },
              onError: reject,
            });
          }),

          // Subcategories
          new Promise((resolve, reject) => {
            fetchSubcategories({
              url: URLS.getAllSubcategories(menuId),
              onSuccess: (data) => {
                try {
                  const extracted = extractResponseData(data, ['subcategories']);
                  resolve(extracted);
                } catch (e) {
                  reject(e);
                }
              },
              onError: reject,
            });
          }),

          // Brands
          new Promise((resolve, reject) => {
            fetchBrands({
              url: URLS.getAllBrands(menuId),
              onSuccess: (data) => {
                try {
                  const extracted = extractResponseData(data, ['brands']);
                  resolve(extracted);
                } catch (e) {
                  reject(e);
                }
              },
              onError: reject,
            });
          }),

          // Product details
          new Promise((resolve, reject) => {
            fetchProductDetails({
              url: URLS.productDetails(productId),
              onSuccess: (data) => {
                try {
                  const extracted = extractResponseData(data, ['product_translations', 'id']);
                  resolve(extracted);
                } catch (e) {
                  reject(e);
                }
              },
              onError: reject,
            });
          }),
        ];

        const [languagesData, categoriesData, subcategoriesData, brandsData, productData] = await Promise.all(promises);

        // Validate that we got the expected data structures
        if (!languagesData || !Array.isArray(languagesData)) {
          console.error('Languages data validation failed:', {
            languagesData,
            isArray: Array.isArray(languagesData),
            type: typeof languagesData,
          });
          throw new Error(`Invalid languages data structure: ${JSON.stringify(languagesData?.slice ? languagesData.slice(0, 50) : languagesData)}`);
        }
        if (!categoriesData || !categoriesData.categories || !Array.isArray(categoriesData.categories)) {
          console.error('Categories data validation failed:', {
            categoriesData,
            hasCategoriesKey: categoriesData?.hasOwnProperty('categories'),
            type: typeof categoriesData,
          });
          throw new Error(`Invalid categories data structure: ${JSON.stringify(categoriesData)}`);
        }
        if (!subcategoriesData || !subcategoriesData.subcategories || !Array.isArray(subcategoriesData.subcategories)) {
          console.error('Subcategories data validation failed:', {
            subcategoriesData,
            hasSubcategoriesKey: subcategoriesData?.hasOwnProperty('subcategories'),
            type: typeof subcategoriesData,
          });
          throw new Error(`Invalid subcategories data structure: ${JSON.stringify(subcategoriesData)}`);
        }
        if (!brandsData || !brandsData.brands || !Array.isArray(brandsData.brands)) {
          console.error('Brands data validation failed:', {
            brandsData,
            hasBrandsKey: brandsData?.hasOwnProperty('brands'),
            type: typeof brandsData,
          });
          throw new Error(`Invalid brands data structure: ${JSON.stringify(brandsData)}`);
        }
        if (!productData || !productData.product_translations || !Array.isArray(productData.product_translations)) {
          console.error('Product data validation failed:', {
            productData,
            hasProductTranslationsKey: productData?.hasOwnProperty('product_translations'),
            type: typeof productData,
            productKeys: productData ? Object.keys(productData).slice(0, 20) : 'N/A',
          });
          throw new Error(`Invalid product data structure: missing product_translations array`);
        }

        console.warn('Validation passed! Starting data transformation...');

        console.warn('Validation passed! Starting data transformation...');

        // Transform data to match expected format
        const transformedCategories = categoriesData.categories.map(cat => ({
          id: cat.id,
          translations: cat.category_translations.map(translation => ({
            lanId: translation.language_id,
            lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
            name: translation.name_text,
          })),
          image: cat.image_url,
          tags: [],
          productsCount: 0,
        }));

        const transformedSubcategories = subcategoriesData.subcategories.map(sub => ({
          id: sub.id,
          categoryId: sub.category_id,
          translations: sub.subcategory_translations.map(translation => ({
            lanId: translation.language_id,
            lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
            name: translation.name_text,
          })),
          image: sub.image_url,
          productsCount: 0,
        }));

        const transformedBrands = brandsData.brands.map(brand => ({
          id: brand.id,
          translations: brand.brand_translations.map(translation => ({
            lanId: translation.language_id,
            lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
            name: translation.name_text,
          })),
          image: brand.image_url,
          productsCount: 0,
        }));

        // Transform product data to match form expectations (similar to prepareSelectedProduct)
        const namesDescriptionsObj = productData.product_translations.reduce((acc, translation) => {
          acc[`name.${translation.language_id}`] = translation.name_text;
          acc[`description.${translation.language_id}`] = translation.description_text;
          return acc;
        }, {});

        const transformedProductData = {
          ...namesDescriptionsObj,
          id: productData.id,
          image: productData.image_url, // Primary image URL
          price: productData.price,
          dineInPrice: productData.dine_in_price,
          priceWholesale: productData.price_wholesale,
          currency: productData.currency,
          discount: productData.discount,
          categoryId: productData.category_id,
          subcategoryId: productData.subcategory_id,
          brandId: productData.brand_id,
          isDeliverable: productData.is_deliverable,
          isInStock: productData.is_in_stock,
          isDinning: productData.is_dinning,
          isRecommended: productData.is_recommended,
          isNew: productData.is_new,
          isSpecialOffer: productData.is_special_offer,
          isTrending: productData.is_trending,
          isBestSeller: productData.is_best_seller,
          images: productData.images_objects || [], // Additional images
          types: productData.types, // Already parsed JSON from API
          colors: productData.colors,
          addons: productData.addons,
          addon_ids: productData.addon_ids || [],
          sizes: productData.sizes, // Already parsed JSON from API
          flavors: productData.flavors,
          calories: productData.calories,
          discount_valid_until: productData.discount_valid_until,
        };

        // Check if selected entities exist in the loaded lists, if not fetch them
        let finalCategories = [...transformedCategories];
        let finalSubcategories = [...transformedSubcategories];
        let finalBrands = [...transformedBrands];

        const entitiesToFetch = [];

        // Check category
        if (transformedProductData.categoryId && !finalCategories.find(cat => cat.id === transformedProductData.categoryId)) {
          entitiesToFetch.push({
            type: 'category',
            id: transformedProductData.categoryId,
            url: URLS.categoryDetails(transformedProductData.categoryId)
          });
        }

        // Check subcategory
        if (transformedProductData.subcategoryId && !finalSubcategories.find(sub => sub.id === transformedProductData.subcategoryId)) {
          entitiesToFetch.push({
            type: 'subcategory',
            id: transformedProductData.subcategoryId,
            url: URLS.subcategoryDetails(transformedProductData.subcategoryId)
          });
        }

        // Check brand
        if (transformedProductData.brandId && !finalBrands.find(brand => brand.id === transformedProductData.brandId)) {
          entitiesToFetch.push({
            type: 'brand',
            id: transformedProductData.brandId,
            url: URLS.brandDetails(transformedProductData.brandId)
          });
        }

        // Fetch missing entities
        if (entitiesToFetch.length > 0) {
          const fetchPromises = entitiesToFetch.map(entity =>
            axios.get(`${baseURL}${entity.url}`, { 
              headers: { 'Authorization': authToken } 
            })
              .then(response => {
                return { ...entity, data: response.data };
              })
              .catch(error => {
                return null; // Don't fail if one entity can't be fetched
              })
          );

          const fetchedEntities = await Promise.all(fetchPromises);

          // Add fetched entities to respective lists
          fetchedEntities.forEach(result => {
            if (result && result.data) {
              // Handle different API response formats
              let entity;
              if (result.type === 'category') {
                entity = result.data.category;
              } else if (result.type === 'subcategory') {
                entity = result.data.subcategory;
              } else if (result.type === 'brand' || result.type === 'brands') {
                entity = result.data.brand;
              } else {
                entity = result.data;
              }

              if (result.type === 'category') {
                finalCategories.push({
                  id: entity.id,
                  translations: entity.category_translations?.map(translation => ({
                    lanId: translation.language_id,
                    lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
                    name: translation.name_text,
                  })) || [],
                  image: entity.image_url,
                  tags: [],
                  productsCount: 0,
                });
              } else if (result.type === 'subcategory') {
                finalSubcategories.push({
                  id: entity.id,
                  categoryId: entity.category_id,
                  translations: entity.subcategory_translations?.map(translation => ({
                    lanId: translation.language_id,
                    lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
                    name: translation.name_text,
                  })) || [],
                  image: entity.image_url,
                  productsCount: 0,
                });
              } else if (result.type === 'brand') {
                finalBrands.push({
                  id: entity.id,
                  translations: entity.brand_translations?.map(translation => ({
                    lanId: translation.language_id,
                    lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
                    name: translation.name_text,
                  })) || [],
                  image: entity.image_url,
                  productsCount: 0,
                });
              }
            }
          });
        }

        setLanguageList(languagesData);
        setCategoryList(finalCategories);
        setSubcategoryList(finalSubcategories);
        setBrandList(finalBrands);
        setSelectedProduct(transformedProductData);

        setHeaderComponent(<b>{`Product ID: ${productId}`}</b>);

      } catch (error) {
        console.error('Error loading product detail data:', error);
        console.error('Error message:', error?.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      setHeaderComponent(null);
    };
  }, [productId, menuId, fetchLanguages, fetchCategories, fetchSubcategories, fetchBrands, fetchProductDetails]);

  const uploadProductImage = async (productData, formValues) => {
    if (
      !formValues?.image?.file?.originFileObj &&
      !(formValues.uploadFileList.length || formValues.deleteFileList.length)
    ) {
      notification.success({
        placement: 'bottomRight',
        message: 'Product updated successfully.',
        duration: 3,
      });
      // Invalidate products cache to refetch lists with the updated product
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['search-products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      navigate('/');
      return;
    } else if (
      !formValues?.image?.file?.originFileObj &&
      (formValues.uploadFileList.length || formValues.deleteFileList.length)
    ) {
      await updateOtherProductImages(productData, formValues);
      notification.success({
        placement: 'bottomRight',
        message: 'Product updated successfully.',
        duration: 3,
      });
      // Invalidate products cache to refetch lists with the updated product
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['search-products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      navigate('/');
      return;
    } else if (
      formValues?.image?.file?.originFileObj &&
      !(formValues.uploadFileList.length || formValues.deleteFileList.length)
    ) {
      await uploadProductImageFetch({
        method: 'FILE_POST',
        url: URLS.uploadProductImage({
          productId: productData?.id,
        }),
        params: { image: formValues?.image?.file?.originFileObj },
        showSuccessNotification: true,
        successMessage: 'Product updated successfully.',
        onSuccess: () => {
          // Invalidate products cache to refetch lists with the updated product
          queryClient.invalidateQueries({ queryKey: ['products'] });
          queryClient.invalidateQueries({ queryKey: ['search-products'] });
          queryClient.invalidateQueries({ queryKey: ['product', productId] });
          navigate('/');
        },
      });
    } else {
      await updateOtherProductImages(productData, formValues);

      await uploadProductImageFetch({
        method: 'FILE_POST',
        url: URLS.uploadProductImage({
          productId: productData?.id,
        }),
        params: { image: formValues?.image?.file?.originFileObj },
        showSuccessNotification: true,
        successMessage: 'Product updated successfully.',
        onSuccess: () => {
          // Invalidate products cache to refetch lists with the updated product
          queryClient.invalidateQueries({ queryKey: ['products'] });
          queryClient.invalidateQueries({ queryKey: ['search-products'] });
          queryClient.invalidateQueries({ queryKey: ['product', productId] });
          navigate('/');
        },
      });
    }
  };

  const updateOtherProductImages = async (productData, formValues) => {
    const formData = new FormData();

    console.log('uploadFileList', formValues.uploadFileList);

    // Add new images (files) to formData
    formValues.uploadFileList.forEach(file => {
      if (!file.url) {
        // Only add newly uploaded images, not existing ones
        formData.append('upload_images', file.originFileObj); // Ensure you're sending the file object
      }
    });

    // Add deleted image IDs to formData
    formValues.deleteFileList.forEach(file => {
      formData.append('delete_images', file.uid); // Send the ID of the deleted images
    });

    try {
      const response = await axios.post(
        `${baseURL}/${URLS.manageProductImages(productData?.id)}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `${authToken}`,
          },
        }
      );
    } catch (error) {
      console.error('Error uploading images:', error);
    }
  };

  const updateProduct = async formValues => {
    const translationsObject = languageList.reduce((acc, language) => {
      acc[language.id] = {
        name_text: formValues[`name.${language.id}`],
        description_text: formValues[`description.${language.id}`],
      };
      return acc;
    }, {});

    let formattedColors =
      formValues.colors != null && Array.isArray(formValues.colors)
        ? formValues.colors.map(color => ({
          ...color,
          hexcode:
            typeof color.hexcode === 'object' ? color.hexcode.toHexString() : color.hexcode,
        }))
        : null;

    await updateProductFetch({
      url: URLS.updateProduct({ productId }),
      method: 'PUT',
      params: {
        ...translationsObject,
        price: formValues.price,
        dine_in_price: formValues.dineInPrice,
        price_wholesale: formValues.priceWholesale,
        currency: formValues.currency,
        discount: formValues.discount,
        calories: formValues.calories,
        category_id: formValues.categoryId,
        subcategory_id: formValues.subcategoryId,
        brand_id: formValues.brandId,
        is_deliverable: formValues.isDeliverable,
        is_in_stock: formValues.isInStock,
        is_dinning: formValues.isDinning,
        is_recommended: formValues.isRecommended,
        is_new: formValues.isNew,
        is_special_offer: formValues.isSpecialOffer,
        is_trending: formValues.isTrending,
        is_best_seller: formValues.isBestSeller,
        types: JSON.stringify(formValues.types),
        colors: JSON.stringify(formattedColors),
        sizes: JSON.stringify(formValues.sizes),
        flavors: JSON.stringify(formValues.flavors || []),
        addon_ids: formValues.addon_ids || [],
      },
      onSuccess: data => uploadProductImage(data, formValues),
    });
  };

  // Search handlers
  const handleSearchCategories = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchCategories([]);
      return;
    }

    setIsSearchingCategories(true);
    try {
      const response = await axios.get(
        `${baseURL}${URLS.searchCategories()}`,
        {
          params: { q: searchQuery, per_page: 20 },
          headers: { Authorization: authToken },
        }
      );

      // Transform search results to match the structure of original categoryList
      let transformedCategories = (response.data.categories || []).map(cat => ({
        id: cat.id,
        translations: cat.category_translations.map(translation => ({
          lanId: translation.language_id,
          lanName: languageList.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
          name: translation.name_text,
        })),
        image: cat.image_url,
        tags: [], // Keep empty for compatibility
        productsCount: 0, // Not available in search results
      }));

      // Ensure currently selected category is included in search results
      if (selectedProduct?.categoryId) {
        const selectedCategoryExists = transformedCategories.find(cat => cat.id === selectedProduct.categoryId) ||
                                       categoryList.find(cat => cat.id === selectedProduct.categoryId);
        if (!selectedCategoryExists) {
          const selectedCategory = categoryList.find(cat => cat.id === selectedProduct.categoryId);
          if (selectedCategory) {
            transformedCategories = [selectedCategory, ...transformedCategories];
          }
        }
      }

      setSearchCategories(transformedCategories);
    } catch (error) {
      console.error('Error searching categories:', error);
      setSearchCategories([]);
    } finally {
      setIsSearchingCategories(false);
    }
  }, [languageList]);

  const handleSearchSubcategories = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchSubcategories([]);
      return;
    }

    setIsSearchingSubcategories(true);
    try {
      const response = await axios.get(
        `${baseURL}${URLS.searchSubcategories()}`,
        {
          params: { q: searchQuery, per_page: 20 },
          headers: { Authorization: authToken },
        }
      );

      // Transform search results to match the structure of original subcategoryList
      let transformedSubcategories = (response.data.subcategories || []).map(sub => ({
        id: sub.id,
        categoryId: sub.category_id,
        translations: sub.subcategory_translations.map(translation => ({
          lanId: translation.language_id,
          lanName: languageList.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
          name: translation.name_text,
        })),
        image: sub.image_url,
        productsCount: 0, // Not available in search results
      }));

      // Ensure currently selected subcategory is included in search results
      if (selectedProduct?.subcategoryId) {
        const selectedSubcategoryExists = transformedSubcategories.find(sub => sub.id === selectedProduct.subcategoryId) ||
                                          subcategoryList.find(sub => sub.id === selectedProduct.subcategoryId);
        if (!selectedSubcategoryExists) {
          const selectedSubcategory = subcategoryList.find(sub => sub.id === selectedProduct.subcategoryId);
          if (selectedSubcategory) {
            transformedSubcategories = [selectedSubcategory, ...transformedSubcategories];
          }
        }
      }

      setSearchSubcategories(transformedSubcategories);
    } catch (error) {
      console.error('Error searching subcategories:', error);
      setSearchSubcategories([]);
    } finally {
      setIsSearchingSubcategories(false);
    }
  }, [languageList]);

  const handleSearchBrands = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchBrands([]);
      return;
    }

    setIsSearchingBrands(true);
    try {
      const response = await axios.get(
        `${baseURL}${URLS.searchBrands()}`,
        {
          params: { q: searchQuery, per_page: 20 },
          headers: { Authorization: authToken },
        }
      );

      // Transform search results to match the structure of original brandList
      let transformedBrands = (response.data.brands || []).map(brand => ({
        id: brand.id,
        translations: brand.brand_translations.map(translation => ({
          lanId: translation.language_id,
          lanName: languageList.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
          name: translation.name_text,
        })),
        image: brand.image_url,
        productsCount: 0, // Not available in search results
      }));

      // Ensure currently selected brand is included in search results
      if (selectedProduct?.brandId) {
        const selectedBrandExists = transformedBrands.find(brand => brand.id === selectedProduct.brandId) ||
                                     brandList.find(brand => brand.id === selectedProduct.brandId);
        if (!selectedBrandExists) {
          const selectedBrand = brandList.find(brand => brand.id === selectedProduct.brandId);
          if (selectedBrand) {
            transformedBrands = [selectedBrand, ...transformedBrands];
          }
        }
      }

      setSearchBrands(transformedBrands);
    } catch (error) {
      console.error('Error searching brands:', error);
      setSearchBrands([]);
    } finally {
      setIsSearchingBrands(false);
    }
  }, [languageList]);

  if (isLoading) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading product details...
        </div>
      </Row>
    );
  }

  if (!selectedProduct) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>Product not found</div>
          <div style={{ color: '#999', fontSize: '12px', marginTop: '10px' }}>
            Please check the product ID and try again
          </div>
        </div>
      </Row>
    );
  }

  return (
    <Row>
      <CreateUpdateProductForm
        selectedProductItem={selectedProduct}
        categoryList={categoryList}
        subcategoryList={subcategoryList}
        brandList={brandList}
        languageList={languageList}
        onSubmit={updateProduct}
        // Search functionality
        searchCategories={searchCategories}
        searchSubcategories={searchSubcategories}
        searchBrands={searchBrands}
        isSearchingCategories={isSearchingCategories}
        isSearchingSubcategories={isSearchingSubcategories}
        isSearchingBrands={isSearchingBrands}
        onSearchCategories={handleSearchCategories}
        onSearchSubcategories={handleSearchSubcategories}
        onSearchBrands={handleSearchBrands}
        addonList={addonsData?.addons || []}
      />
    </Row>
  );
}

export default ProductDetail;
